import {Dispatch, FC, SetStateAction, useEffect, useState} from 'react'
import {CustomInputs} from '../../../../../_metronic/helpers/components/CustomInputs'
import {API_URL, fixNumbers} from '../../../../../_metronic/helpers'
import axios from 'axios'
import {BuyerInquiryState, classifyBuyerInquiryError} from './buyerInquiryError'

const createInquiryCorrelationId = () => {
  const randomPart = Math.random().toString(36).slice(2, 12)
  return `buyer-${Date.now().toString(36)}-${randomPart}`
}

export interface IInqueryEconomicCode {
  nId: string
  ecd: string
  nm: string
  tp: number | string
  st: string
  hs: boolean
  address?: string
  zipCode?: string
}

type props = {
  inquerydata: {
    inquerydata: IInqueryEconomicCode | undefined
    setInqueryData: Dispatch<SetStateAction<IInqueryEconomicCode | undefined>>
  }
  loading: {
    loading: boolean
    setLoading: Dispatch<SetStateAction<boolean>>
  }
  economicCode: {
    economicCode: string
    setEconomicCode: Dispatch<SetStateAction<string>>
  }
  nationalCode: {
    nationalCode: string
    setNationalCode: Dispatch<SetStateAction<string>>
  }
  expectedEconomicCodeLength?: number
  autoInquire?: boolean
  validationError?: string
  isRequired?: boolean
  onInquiryStateChange?: (state: BuyerInquiryState) => void
}

const InqueryEconomicCode: FC<props> = ({
  inquerydata,
  loading,
  economicCode,
  nationalCode,
  expectedEconomicCodeLength,
  autoInquire = true,
  validationError,
  isRequired = true,
  onInquiryStateChange,
  //title,
}) => {
  const [message, setMessage] = useState('')
  const [first, setFirst] = useState(true)

  const inqueryNationalId = async () => {
    const correlationId = createInquiryCorrelationId()
    loading.setLoading(true)
    setMessage('')
    onInquiryStateChange?.('pending')

    try {
      const response = await axios.post(
        `${API_URL}/auth/taxInqueryCompany`,
        {
          economicCode: economicCode.economicCode,
          nationalCode: nationalCode.nationalCode,
        },
        {headers: {'X-Correlation-ID': correlationId}}
      )
      const data: IInqueryEconomicCode = response.data as IInqueryEconomicCode
      inquerydata.setInqueryData(data)
      economicCode.setEconomicCode(data.ecd || economicCode.economicCode)
      nationalCode.setNationalCode(data.nId || '')
      onInquiryStateChange?.('success')
    } catch (error: unknown) {
      const failure = classifyBuyerInquiryError(error, correlationId)
      setMessage(
        failure.traceId ? `${failure.message} شناسه پیگیری: ${failure.traceId}` : failure.message
      )
      inquerydata.setInqueryData(undefined)
      onInquiryStateChange?.(failure.state)
    } finally {
      loading.setLoading(false)
    }
  }

  useEffect(() => {
    setMessage(validationError || '')
    economicCode.setEconomicCode(fixNumbers(economicCode.economicCode).replace(/[^0-9]+/gi, ''))
  }, [economicCode.economicCode, validationError])

  useEffect(() => {
    if (first) {
      const hasExpectedLength = expectedEconomicCodeLength
        ? economicCode.economicCode.length === expectedEconomicCodeLength
        : economicCode.economicCode.length === 11 || economicCode.economicCode.length === 14
      if (autoInquire && hasExpectedLength) {
        inqueryNationalId()
      }
      setFirst(false)
    }
  }, [first])

  const hasValidEconomicCodeLength = expectedEconomicCodeLength
    ? economicCode.economicCode.length === expectedEconomicCodeLength
    : economicCode.economicCode.length === 11 || economicCode.economicCode.length === 14

  return (
    <>
      <div className='row'>
        <div className='col-12'>
          <CustomInputs
            //label={title ? title : 'شناسه ملی/شماره اقتصادی را وارد کنید :'}
            label={
              <span>
                <span className='fw-bolder' style={{textDecoration: 'underline'}}>
                  شماره اقتصادی
                </span>{' '}
                کسب و کار را وارد کنید
              </span>
            }
            LTR
            isRequired={isRequired}
            error={message}
            max={14}
            isDisabled={loading.loading || inquerydata.inquerydata != undefined}
            stateValue={{value: economicCode.economicCode, setValue: economicCode.setEconomicCode}}
            type={'text'}
          >
            {inquerydata.inquerydata ? (
              <>
                <button
                  className='btn btn-light-warning'
                  disabled={loading.loading || !hasValidEconomicCodeLength}
                  onClick={() => {
                    inquerydata.setInqueryData(undefined)
                    nationalCode.setNationalCode('')
                    economicCode.setEconomicCode('')
                    onInquiryStateChange?.('idle')
                  }}
                >
                  <span>
                    <i className='las la-undo-alt me-2' />
                    تغییر
                  </span>
                </button>
              </>
            ) : (
              <>
                <button
                  className='btn btn-success py-0 mb-3 mb-lg-0'
                  disabled={loading.loading || !hasValidEconomicCodeLength || !!validationError}
                  onClick={inqueryNationalId}
                >
                  {loading.loading ? (
                    <span>
                      <i className='las la-circle-notch la-spin fs-3 me-2' />
                      استعلام...
                    </span>
                  ) : (
                    <span>
                      <i className='las la-search fs-3 me-2' />
                      استعلام
                    </span>
                  )}
                </button>
              </>
            )}
          </CustomInputs>
        </div>
        {inquerydata.inquerydata && (
          <div className='col-12 mt-5'>
            <div className='border border-1 mt-2 border-primary rounded border-dashed bg-light-primary'>
              <ul className='list mt-5 text-gray-700' style={{listStyle: 'none'}}>
                <li>
                  {inquerydata.inquerydata.nId.length == 11 ? 'شناسه ملی' : 'کد ملی'}:{' '}
                  <b className='text-primary ms-3'>{inquerydata.inquerydata.nId}</b>
                </li>
                <li>
                  کد اقتصادی
                  <b className='text-primary ms-3'>{inquerydata.inquerydata.ecd}</b>
                </li>
                <li>
                  نام:{' '}
                  <span className='text-primary ms-3'>
                    <b className='mx-1'>{inquerydata.inquerydata.nm}</b>{' '}
                    {`(${inquerydata.inquerydata.st})`}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export {InqueryEconomicCode}
